import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Card, Typography, Select, Upload, Modal, Row, Col, Spin } from "antd";
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined, HomeOutlined, BankOutlined, UploadOutlined, GlobalOutlined } from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import EnterpriseLoyaltyAPI from "../../../api/EnterpriseLoyalty";
import "./EnterpriseSignup.scss";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const EnterpriseSignup = () => {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const history = useHistory();
  const location = useLocation();
  const [prefilledData, setPrefilledData] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);


  // Currency options
  const currencies = [
    { code: "NGN", name: "Nigerian Naira (₦)" },
    { code: "USD", name: "US Dollar ($)" },
    { code: "GBP", name: "British Pound (£)" },
    { code: "EUR", name: "Euro (€)" },
    { code: "ZAR", name: "South African Rand (R)" },
    { code: "GHS", name: "Ghanaian Cedi (₵)" },
    { code: "KES", name: "Kenyan Shilling (KSh)" },
  ];



  useEffect(() => {
    // Check if we have prefilled data from merchant login
    if (location.state && location.state.merchantData) {
      const merchantData = location.state.merchantData;
      setPrefilledData(merchantData);
      
      // Prefill form with merchant data (including password)
      form.setFieldsValue({
        organisation_name: merchantData.business_name || "",
        organisation_email: merchantData.email || "",
        password: merchantData.password || "",
        confirm_password: merchantData.password || "",
        contact_person: merchantData.first_name && merchantData.last_name 
          ? `${merchantData.first_name} ${merchantData.last_name}` 
          : "",
        contact_email: merchantData.email || "",
        contact_phone_number: merchantData.contact_number || "",
        organisation_phone_number: merchantData.contact_number || "",
        organisation_address: merchantData.address_line1 || "",
        state: merchantData.state || "",
        country: merchantData.country || "",
        currency: merchantData.currency || "NGN",
      });
      if (merchantData.country) setSelectedCountry(merchantData.country);
      if (merchantData.state) setSelectedState(merchantData.state);
    }
  }, [location, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Prepare organization data
      const organizationData = {
        organisation_email: values.organisation_email,
        password: values.password,
        organisation_name: values.organisation_name,
        currency: values.currency,
        organisation_phone_number: values.organisation_phone_number,
        contact_phone_number: values.contact_phone_number,
        contact_person: values.contact_person,
        contact_email: values.contact_email,
        organisation_address: values.organisation_address,
        state: values.state,
        country: values.country,

      };

      // Step 1: Create organization account
      const orgResponse = await EnterpriseLoyaltyAPI.createOrganization(organizationData);
      
      // Extract organization ID from response.data
      const organizationId = orgResponse.data?.organisation_id || orgResponse.organisation_id || orgResponse.organisation?.id;
      
      if (!organizationId) {
        console.error("Full response:", orgResponse);
        throw new Error("Organization created but no ID returned");
      }
      message.success("Organization account created successfully!");

      // Step 2: If this is a new account (not from existing merchant), create merchant account
      if (!prefilledData) {
        const merchantData = {
          first_name: values.contact_person.split(" ")[0] || values.contact_person,
          last_name: values.contact_person.split(" ").slice(1).join(" ") || "",
          contact_number: values.contact_phone_number,
          business_name: values.organisation_name,
          business_type: "Other",
          address_line1: values.organisation_address,
          address_line2: "",
          postcode: "",
          currency: values.currency,
          country: values.country,
          state: values.state,
          organization_id: organizationId,
          signup_source: "enterprise_suite",
          email: values.organisation_email,
          password: values.password,
          password_confirmation: values.password,
        };

        try {
          await EnterpriseLoyaltyAPI.createMerchantAccount(merchantData);
          message.success("Merchant account created successfully!");
        } catch (merchantError) {
          console.error("Merchant creation error:", merchantError);
          message.warning("Organization created, but merchant account creation encountered an issue. You can still proceed.");
        }
      } else {
        // If prefilled from existing merchant, we need to link the organization_id
        message.info("Linking your existing merchant account with organization...");
      }

      // Step 3: Auto-login with the new credentials
      try {
        await EnterpriseLoyaltyAPI.organizationLogin(
          values.organisation_email,
          values.password
        );
        
        message.success("Welcome to Loystar Enterprise Suite!");
        setIsRedirecting(true);
        setTimeout(() => {
          history.push("/e/dashboard");
        }, 1500);
      } catch (loginError) {
        console.error("Auto-login failed:", loginError);
        message.info("Account created successfully! Please login to continue.");
        history.push("/e/login");
      }

    } catch (error) {
      console.error("Signup failed:", error);
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || "Failed to create organization account. Please try again.";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="enterprise-signup-container">
      <div className="enterprise-signup-background">
        <div className="background-overlay" />
      </div>
      
      <div className="enterprise-signup-content">
        <Card className="signup-card">
          <div className="signup-header">
            <div className="logo-container">
              <img 
                src="/loystar-favicon-png.png" 
                alt="Loystar" 
                className="signup-logo"
              />
            </div>
            <Title level={2} className="signup-title">
              Create Your Organization
            </Title>
            <Text className="signup-subtitle">
              {prefilledData 
                ? "Complete your organization profile to get started"
                : "Join the Loystar Enterprise Loyalty Suite"}
            </Text>
          </div>

          <Form
            form={form}
            name="enterprise-signup"
            onFinish={handleSubmit}
            layout="vertical"
            className="signup-form"
            size="large"
            scrollToFirstError
          >
            {/* Organization Details Section */}
            <div className="form-section">
              <Title level={4} className="section-title">Organization Details</Title>
              
              <Form.Item
                name="organisation_name"
                label="Organization Name"
                rules={[
                  { required: true, message: "Please enter your organization name" },
                ]}
              >
                <Input
                  prefix={<BankOutlined className="input-icon" />}
                  placeholder="My Company Ltd"
                />
              </Form.Item>

              <Form.Item
                name="organisation_email"
                label="Organization Email"
                rules={[
                  { required: true, message: "Please enter organization email" },
                  { type: "email", message: "Please enter a valid email address" },
                ]}
              >
                <Input
                  prefix={<MailOutlined className="input-icon" />}
                  placeholder="contact@company.com"
                  disabled={!!prefilledData}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: "Please enter a password" },
                  { min: 6, message: "Password must be at least 6 characters" },
                  { 
                    pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~])/,
                    message: "Password must contain at least one uppercase letter, one number, and one symbol"
                  }
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined className="input-icon" />}
                  placeholder="Enter secure password"
                  disabled={!!prefilledData}
                />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                label="Confirm Password"
                dependencies={['password']}
                hasFeedback
                rules={[
                  { required: true, message: "Please confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="input-icon" />}
                  placeholder="Confirm your password"
                  disabled={!!prefilledData}
                />
              </Form.Item>

              <Form.Item
                name="organisation_phone_number"
                label="Organization Phone Number"
                rules={[
                  { required: true, message: "Please enter organization phone number" },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined className="input-icon" />}
                  placeholder="+234834567788"
                />
              </Form.Item>

              <Form.Item
                name="currency"
                label="Currency"
                rules={[
                  { required: true, message: "Please select a currency" },
                ]}
              >
                <Select placeholder="Select currency">
                  {currencies.map(curr => (
                    <Option key={curr.code} value={curr.code}>
                      {curr.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Contact Person Section */}
            <div className="form-section">
              <Title level={4} className="section-title">Contact Person</Title>
              
              <Form.Item
                name="contact_person"
                label="Full Name"
                rules={[
                  { required: true, message: "Please enter contact person name" },
                ]}
              >
                <Input
                  prefix={<UserOutlined className="input-icon" />}
                  placeholder="John Doe"
                />
              </Form.Item>

              <Form.Item
                name="contact_email"
                label="Email"
                rules={[
                  { required: true, message: "Please enter contact email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input
                  prefix={<MailOutlined className="input-icon" />}
                  placeholder="john@company.com"
                />
              </Form.Item>

              <Form.Item
                name="contact_phone_number"
                label="Phone Number"
                rules={[
                  { required: true, message: "Please enter contact phone number" },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined className="input-icon" />}
                  placeholder="+234834567788"
                />
              </Form.Item>
            </div>

            {/* Address Section */}
            <div className="form-section">
              <Title level={4} className="section-title">Organization Address</Title>
              
              <Form.Item
                name="organisation_address"
                label="Street Address"
                rules={[
                  { required: true, message: "Please enter organization address" },
                ]}
              >
                <TextArea
                  prefix={<HomeOutlined className="input-icon" />}
                  placeholder="123 Main Street"
                  rows={2}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="country"
                    label="Country"
                    rules={[
                      { required: true, message: "Please select country" },
                    ]}
                  >
                    <CountryDropdown
                      value={selectedCountry}
                      onChange={(val) => {
                        setSelectedCountry(val);
                        form.setFieldsValue({ country: val, state: "" });
                        setSelectedState("");
                      }}
                      classes="ant-input ant-input-lg"
                      defaultOptionLabel="Select Country"
                      priorityOptions={["Nigeria", "United Kingdom", "United States"]}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="state"
                    label="State/Province"
                    rules={[
                      { required: true, message: "Please select state" },
                    ]}
                  >
                    <RegionDropdown
                      country={selectedCountry}
                      value={selectedState}
                      onChange={(val) => {
                        setSelectedState(val);
                        form.setFieldsValue({ state: val });
                      }}
                      classes="ant-input ant-input-lg"
                      defaultOptionLabel="Select State"
                      blankOptionLabel="Select Country first"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>



            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="signup-button"
                loading={loading}
                block
              >
                {loading ? "Creating Organization..." : "Create Organization"}
              </Button>
            </Form.Item>
          </Form>

          <div className="signup-footer">
            <Text className="footer-text">
              Already have an account? <a onClick={() => history.push("/e/login")}>Sign In</a>
            </Text>
          </div>
        </Card>

        <div className="powered-by">
          <Typography.Text className="powered-text">
            Powered by Loystar Enterprise © {new Date().getFullYear()}
          </Typography.Text>
        </div>
      </div>

      <Modal
        open={loading || isRedirecting}
        footer={null}
        closable={false}
        centered
        className="signup-loading-modal"
        width={300}
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 20 }}>
            <Typography.Title level={4}>
              {isRedirecting ? "Preparing your dashboard..." : "Creating your organization..."}
            </Typography.Title>
            <Typography.Text type="secondary">
              Please wait a moment.
            </Typography.Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EnterpriseSignup;
